import { AppTextInput, FieldRow } from 'components/FormElements';
import Spacing from 'components/Spacing';
import { Controller, useFormContext } from 'react-hook-form';
import { lpCompany as rules } from 'further-ui/validations';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import { useNotification } from 'hooks/ui/useNotification';
import useFileUpload, { UploadFileResult } from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import CompanyStageSelector from 'components/CompanyStageSelector';
import CompanySectorSelector from 'components/CompanySectorSelector';

const PortalInformation: React.FC = () => {
  const { control } = useFormContext<rules.CompanySchemaFormValues>();
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();

  const handleFileUpload = async (
    files: Array<UploadedFile>,
    onSuccess: (uploadFileResult: UploadFileResult) => void,
  ) => {
    if (!files?.[0]?.path) return;
    const uploadFileResult = await uploadFile(
      files[0],
      UploadType.CompanyLogos,
    );
    if (uploadFileResult.data) {
      onSuccess(uploadFileResult.data);
    } else {
      error(uploadFileResult.error.message);
    }
  };
  return (
    <Spacing>
      <FieldRow title="Description">
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppTextInput
              {...field}
              multiline
              placeholder="Enter description of the company in approximately 30-40 words"
              rows={3}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Logo (square 1:1)" centerTitle>
        <Controller
          name="companyLogo"
          control={control}
          render={({ field: { ref, onChange, value, ...rest } }) => (
            <Dropzone
              {...rest}
              ref={ref}
              maxFiles={1}
              defaultAcceptGroup="images"
              onDropFile={async (files) => {
                await handleFileUpload(files, (uploadFileResult) => {
                  onChange({
                    path: uploadFileResult.filePath,
                    name: uploadFileResult.name,
                    preview: uploadFileResult.url,
                  });
                });
              }}
              files={
                value
                  ? [
                      {
                        preview: value.preview,
                        fileName: value.name,
                      },
                    ]
                  : []
              }
              onRemove={() => {
                onChange(null);
              }}
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Website">
        <Controller
          name="websiteUrl"
          control={control}
          render={({ field }) => (
            <AppTextInput {...field} placeholder="Enter website URL" />
          )}
        />
      </FieldRow>
      <FieldRow title="Twitter/X URL">
        <Controller
          name="twitterUrl"
          control={control}
          render={({ field }) => (
            <AppTextInput {...field} placeholder="Enter Twitter/X URL" />
          )}
        />
      </FieldRow>
      <FieldRow title="LinkedIn URL">
        <Controller
          name="linkedinUrl"
          control={control}
          render={({ field }) => (
            <AppTextInput {...field} placeholder="Enter LinkedIn URL" />
          )}
        />
      </FieldRow>
      <FieldRow title="Stage">
        <Controller
          name="stage"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CompanyStageSelector
              value={value}
              onChange={onChange}
              name="stage"
            />
          )}
        />
      </FieldRow>
      <FieldRow title="Sector">
        <Controller
          name="sector"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CompanySectorSelector
              value={value}
              onChange={onChange}
              name="sector"
            />
          )}
        />
      </FieldRow>
    </Spacing>
  );
};

export default PortalInformation;
